import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Breadcrumbs, buildBreadcrumbLd } from "@/components/site/Breadcrumbs";
import { socialImageMeta } from "@/lib/social";
import { countryImage } from "@/lib/country-image";
import { makeSeoTitle } from "@/lib/seo-title";
import { makeSeoDescription } from "@/lib/seo-description";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { ShareBar } from "@/components/site/ShareBar";
import {
  COUNTRY_EVENTS,
  getCountryEvents,
  type CountryEvent,
} from "@/data/country-events";
import { roundFlagUrl, type CountrySlug } from "@/data/countries";
import { CalendarDays, Clock, MapPin, Ticket, Compass, ChevronLeft, Lightbulb } from "lucide-react";

const VALID = new Set(COUNTRY_EVENTS.map((c) => c.slug));

export const Route = createFileRoute("/events/$country")({
  beforeLoad: ({ params }) => {
    if (!VALID.has(params.country)) throw notFound();
  },
  head: ({ params }) => {
    const data = getCountryEvents(params.country);
    if (!data) return {};
    const url = `https://lifewestway.com/events/${data.slug}`;
    const title = makeSeoTitle(`${data.name} Events 2026`);
    const description = makeSeoDescription(
      `${data.name} events calendar for expats — dates, start times, venues, ticket prices and official booking or RSVP links for ${data.events.length} major events.`
    );
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:site_name", content: "LifeWestWay" },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...socialImageMeta({
          image: countryImage(data.slug),
          alt: `${data.name} events calendar 2026 — festival, sport and culture dates for expats`,
        }),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbLd([
              { label: "Home", href: "/" },
              { label: "Events", href: "/events" },
              { label: data.name },
            ])
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            data.events.map((e) => ({
              "@context": "https://schema.org",
              "@type": "Event",
              name: e.name,
              startDate: e.startDate,
              ...(e.endDate ? { endDate: e.endDate } : {}),
              eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
              eventStatus: "https://schema.org/EventScheduled",
              description: e.description,
              location: {
                "@type": "Place",
                name: e.venue,
                address: { "@type": "PostalAddress", streetAddress: e.address, addressLocality: e.city },
              },
              offers: {
                "@type": "Offer",
                url: e.ticketUrl,
                availability: "https://schema.org/InStock",
                description: e.priceLabel,
              },
              organizer: { "@type": "Organization", name: e.name, url: e.ticketUrl },
            }))
          ),
        },
      ],
    };
  },
  component: CountryEventsPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-foreground">Events page not found</h1>
      <p className="mt-3 text-muted-foreground">
        We do not have an events calendar for that destination yet.
      </p>
      <Link title="See all country event calendars" to="/events" className="mt-6 inline-block text-accent underline">
        Browse all country events
      </Link>
    </main>
  ),
});

function EventCard({ event }: { event: CountryEvent }) {
  return (
    <article className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-accent/60 hover:shadow-md">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full border border-accent/40 bg-accent/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-accent">
          {event.category}
        </span>
        {event.rsvp && (
          <span className="rounded-full border border-border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/70">
            Free / RSVP
          </span>
        )}
      </div>

      <h3 className="mt-3 font-display text-xl font-bold text-foreground">{event.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground/75">{event.description}</p>

      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="flex gap-2">
          <CalendarDays aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-foreground/55">Date</dt>
            <dd className="text-sm text-foreground/85">
              <time dateTime={event.startDate}>{event.dateLabel}</time>
            </dd>
          </div>
        </div>
        <div className="flex gap-2">
          <Clock aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-foreground/55">Time</dt>
            <dd className="text-sm text-foreground/85">{event.timeLabel}</dd>
          </div>
        </div>
        <div className="flex gap-2">
          <MapPin aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-foreground/55">Location</dt>
            <dd className="text-sm text-foreground/85">
              {event.venue}, {event.city}
              <span className="block text-xs text-foreground/60">{event.address}</span>
            </dd>
          </div>
        </div>
        <div className="flex gap-2">
          <Ticket aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-foreground/55">Entry</dt>
            <dd className="text-sm text-foreground/85">{event.priceLabel}</dd>
          </div>
        </div>
      </dl>

      <h4 className="mt-5 flex items-center gap-1.5 font-display text-sm font-bold text-accent">
        <Lightbulb aria-hidden className="h-4 w-4" /> Newcomer tip
      </h4>
      <p className="mt-1 text-sm leading-relaxed text-foreground/70">{event.expatTip}</p>

      <a
        href={event.ticketUrl}
        target="_blank"
        rel="noopener"
        title={`${event.ticketLabel} — official ${event.name} page`}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-navy transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <Ticket aria-hidden className="h-4 w-4" />
        {event.rsvp ? `${event.ticketLabel} (free / RSVP)` : event.ticketLabel}
      </a>
    </article>
  );
}

function CountryEventsPage() {
  const { country } = Route.useParams();
  const data = getCountryEvents(country)!;
  const others = COUNTRY_EVENTS.filter((c) => c.slug !== data.slug);

  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[{ label: "Home", href: "/" }, { label: "Events", href: "/events" }, { label: data.name }]}
      />

      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src={roundFlagUrl(data.code)} alt={`${data.name} flag — ${data.name} events calendar 2026`} width={44} height={44} className="h-11 w-11 rounded-full ring-2 ring-accent/40" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
              {data.events.length} events · prices in {data.currency}
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {data.name} <span className="text-accent">Events 2026</span> — dates, venues &amp; tickets
          </h1>
          <p className="mt-4 max-w-3xl text-base text-foreground/75 sm:text-lg">{data.intro}</p>
        </div>
      </section>

      <h2 className="mt-12 font-display text-2xl font-bold text-accent">Planning notes for {data.name}</h2>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground/75">{data.planning}</p>

      <h2 className="mt-12 font-display text-2xl font-bold text-accent">
        Upcoming events in {data.name}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-foreground/70">
        Listed in date order. Prices are the organiser&apos;s published starting rates and change
        between release tiers — always confirm on the official ticket or RSVP page before booking
        flights or accommodation.
      </p>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        {data.events.map((e) => (
          <EventCard key={e.id} event={e} />
        ))}
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold text-accent">Before you book</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-base font-bold text-foreground">Check your visa conditions</h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/70">
            Attending events is fine on almost any status, but paid work at one — including
            stewarding, bar shifts or paid performance — is not always permitted on a visitor or
            study permit. If an event is also a work opportunity, confirm your right-to-work
            conditions first.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-display text-base font-bold text-foreground">Buy from the organiser</h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/70">
            Every link on this page points to the official organiser, venue or tourism authority.
            Resale platforms routinely list tickets that the organiser will refuse at the gate,
            particularly for named-ticket events like marathons and Grand Slam tennis.
          </p>
        </div>
      </div>

      {(["usa", "uk", "australia", "italy", "portugal", "canada"] as CountrySlug[]).includes(
        data.slug as CountrySlug
      ) && (
        <div className="mt-10 rounded-2xl border border-accent/30 bg-accent/5 p-6">
          <h2 className="font-display text-xl font-bold text-foreground">
            Moving to {data.name}, not just visiting?
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-foreground/75">
            Our full country hub covers visas, jobs, healthcare, taxes and cost of living with
            2026 figures throughout.
          </p>
          <Link
            to="/$country"
            params={{ country: data.slug as CountrySlug }}
            title={`Open the full ${data.name} guide`}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-accent hover:underline"
          >
            <Compass className="h-4 w-4" /> Full {data.name} guide
          </Link>
        </div>
      )}

      <h2 className="mt-14 font-display text-2xl font-bold text-accent">Events in other countries</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {others.map((c) => (
          <Link
            key={c.slug}
            to="/events/$country"
            params={{ country: c.slug }}
            title={`See events in ${c.name}`}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground/80 transition-colors hover:border-accent/60 hover:text-accent"
          >
            <img src={roundFlagUrl(c.code)} alt={`${c.name} flag`} width={18} height={18} loading="lazy" className="h-4.5 w-4.5 rounded-full" />
            {c.name}
          </Link>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <Link to="/events" title="Back to the global events calendar" className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-accent">
          <ChevronLeft className="h-4 w-4" /> All country calendars
        </Link>
        <ShareBar title={`${data.name} events 2026`} />
      </div>

      <ArticleDisclaimer />
    </main>
  );
}
